import {
  createRootRouteWithContext,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import { SplashScreen } from '@capacitor/splash-screen'
import { useAuth } from '@/hooks/useAuth'
import { useEffect } from 'react'
import { Toaster } from '@/components/ui/sonner'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Loading } from '@/components/ui/loading'
import { useConversationsWidget } from '@/hooks/useConversationsWidget'

interface RouterContext {
  isAuthenticated: boolean
  userIsAdmin: boolean
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
})

// Routes that don't require authentication
// /oauth/consent sends signed-out users to /signin itself, keeping its redirect.
const PUBLIC_ROUTES = ['/', '/signin', '/onboarding', '/auth/callback', '/oauth/consent']

function RootComponent() {
  const { isLoaded, isSignedIn } = useAuth()
  const navigate = useNavigate()
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  // The PostHog Conversations chat bubble is only wanted on Help & Support.
  useConversationsWidget(currentPath === '/profile/help-support')

  // Hide splash screen once auth is loaded
  useEffect(() => {
    if (isLoaded) {
      document.getElementById('splash-screen')?.remove()
      SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {})
    }
  }, [isLoaded])

  // Single redirect rule: unauthenticated users on protected routes go to /signin
  useEffect(() => {
    if (!isLoaded) return

    const browserPath = window.location.pathname
    const isPublic = PUBLIC_ROUTES.some(route =>
      route === '/' ? browserPath === '/' : browserPath.startsWith(route),
    )

    if (!isSignedIn && !isPublic) {
      navigate({ to: '/signin' })
    }
  }, [isLoaded, isSignedIn, currentPath, navigate])

  if (!isLoaded) {
    return (
      <div className='h-screen'>
        <Loading />
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div
        className="h-full flex flex-col overflow-hidden"
      >
        <Outlet />
      </div>
      <Toaster position="top-center" />
    </ErrorBoundary>
  )
}
